<template>
  <div>
    <router-link
      tag="a"
      class="nav-item-hold"
      to="#"
      @click.native="showQuickAddModal"
    >
      <i class="nav-icon i-Notepad"></i>
      <span class="nav-text">Quick Add Task</span>
    </router-link>

    <!-- Quick Add MODAL -->
    <b-modal
      ref="quickAddModal"
      size="lg"
      title="Add Task"
      header-text-variant="dark"
      ok-title="Submit"
      cancel-title="Cancel"
      cancel-variant="danger"
      @ok.prevent="validateQuickAdd"
      ><div>
        <b-form-group label="Client">
          <b-form-select v-model="quickAddClient" :options="clientOptions">
            <template v-slot:first>
              <option :value="null" disabled
                >-- Please select a Client --</option
              >
            </template>
          </b-form-select>
        </b-form-group>
        <b-form-group v-if="quickAddClient">
          <b-form-checkbox v-model="useExistingSection"
            >Use Existing Section?</b-form-checkbox
          >
        </b-form-group>
        <template v-if="quickAddClient">
          <b-form-group label="Section">
            <b-form-select
              v-if="useExistingSection"
              v-model="quickAddSection"
              :options="sectionOptions"
            >
              <template v-slot:first>
                <option :value="null" disabled
                  >-- Please select a Section --</option
                >
              </template>
            </b-form-select>
            <b-form-input
              v-else
              v-model="newSectionName"
              placeholder="Input new section name here"
            >
            </b-form-input>
          </b-form-group>
          <b-form-group v-if="users.length" label="Task">
            <b-row>
              <b-col md="6">
                <b-form-input
                  v-model="newTaskDescription"
                  placeholder="Input description here"
                ></b-form-input>
              </b-col>
              <b-col md="6">
                <VueTagsInput
                  ref="usersTags"
                  v-model="selectedUser"
                  :tags="tagUsers"
                  :autocomplete-items="items"
                  :add-only-from-autocomplete="true"
                  class="tags-input tags-custom list-item-tags-input"
                  :placeholder="tagUsers.length ? '' : 'assignee'"
                  @tags-changed="newTags => (tagUsers = newTags)"
                >
                  <div
                    slot="autocomplete-item"
                    slot-scope="props"
                    @click="props.performAdd(props.item)"
                  >
                    {{ props.item.text }}
                  </div>
                  <div slot="tag-center" slot-scope="props">
                    <ProjectAvatar
                      :user="getUserById(props.tag.id)"
                      :hide-nick-name="true"
                      :width="24"
                      :height="24"
                      :only-initials="true"
                    />
                  </div>
                </VueTagsInput>
              </b-col>
            </b-row>
          </b-form-group>
        </template></div
    ></b-modal>
  </div>
</template>

<script>
import ProjectAvatar from "@/components/projectAvatar"
import { mapGetters } from "vuex"
import gql from "graphql-tag"

export default {
  name: "QuickAddTask",
  components: {
    ProjectAvatar
  },
  apollo: {
    clients: {
      query: gql`
        query {
          clients {
            id
            name
            sections {
              id
              name
            }
          }
        }
      `,
      update(data) {
        return data?.clients.sortBy(x => x.name)
      },
      skip() {
        //Only load query once modal has been opened
        return !this.loadClients
      }
    },
    users: {
      query: gql`
        query {
          users {
            firstName
            lastName
            id
          }
        }
      `,
      update(data) {
        return data.users?.removeUnassigned() ?? []
      },
      skip() {
        //Only load query once clients have been loaded
        return !this.clients
      }
    },
    userClients: {
      query: gql`
        query userClients($userId: String!) {
          userClients(userId: $userId) {
            name
            id
          }
        }
      `,
      variables() {
        return {
          userId: this.getCurrentUser.id
        }
      },
      skip() {
        //Only load query once modal has been opened
        return !this.loadClients
      }
    }
  },
  data() {
    return {
      loadClients: false,
      newSectionName: null,
      newTaskDescription: null,
      selectedUser: "",
      tagUsers: [],
      quickAddClient: null,
      quickAddSection: null,
      useExistingSection: true
    }
  },
  computed: {
    ...mapGetters({
      getCurrentUser: "getCurrentUser",
      getUserById: "getUserById"
    }),
    clientOptions() {
      const yourClients = {
        label: "Your Clients",
        options: [
          ...(this.userClients?.map(x => {
            return { value: x, text: x.name }
          }) ?? [])
        ]
      }

      return [
        yourClients,
        ...(this.clients?.map(x => {
          return { value: x, text: x.name }
        }) ?? [])
      ]
    },
    sectionOptions() {
      //Doing this so I dont have to call the api twice for 1 clients sections
      let sections = this.quickAddClient?.sections
      if (!sections)
        sections = this.clients.find(x => x.id == this.quickAddClient.id)
          ?.sections

      return sections
        ?.sortBy(x => x.name)
        ?.map(section => {
          return {
            value: section.id,
            text: section.name
          }
        })
    },
    items() {
      return (
        this.users
          ?.filter(i => {
            return (
              !this.tagUsers.map(x => x.id).includes(i.id) &&
              i.firstName?.includesLowerCase(this.selectedUser)
            )
          })
          .map(x => {
            return {
              id: x.id,
              text: this.$utils.getUserName(x)
            }
          }) ?? []
      )
    }
  },
  watch: {
    quickAddClient() {
      this.quickAddSection = null
      this.newSectionName = null
    }
  },
  created() {},
  methods: {
    /**
     * Performs the quick-add
     * @returns {Promise<void>}
     */
    async quickAddTask() {
      let task = {
        userId: this.getCurrentUser.id,
        number: 0,
        description: this.newTaskDescription,
        clientId: this.quickAddClient.id,
        users: this.tagUsers?.map(x => x.id) ?? []
      }
      if (this.useExistingSection) task.sectionId = this.quickAddSection
      else task.newSectionName = this.newSectionName

      await this.$apollo.mutate({
        mutation: require("@/graphql/Task/QuickAddTask.gql"),
        variables: {
          task
        }
      })

      this.quickAddTaskSuccess()
    },
    /**
     * Lets user know the quick-add was successful
     */
    quickAddTaskSuccess() {
      this.$toasted.success("Task added")
      this.$refs.quickAddModal.hide()

      //Clear form
      this.quickAddClient = null
      this.newTaskDescription = null
      this.useExistingSection = true
      this.newSectionName = null
      this.selectedUser = ""
      this.tagUsers = []
    },
    showQuickAddModal() {
      //Improvised lazy loading of clients dropdown
      this.loadClients = true
      this.$refs.quickAddModal.show()
    },
    /**
     * Validates inputs for quick-adding a task
     * @returns {Promise<ToastObject>}
     */
    async validateQuickAdd() {
      if (!this.quickAddClient)
        return this.$toasted.error("Please select a client")

      if (this.useExistingSection && !this.quickAddSection)
        return this.$toasted.error(
          "Please select an existing Section or create a new one"
        )

      if (!this.newSectionName && !this.useExistingSection)
        return this.$toasted.error("Please enter new Section name")

      if (!this.newTaskDescription)
        return this.$toasted.error("Please add description for this task")

      if (!this.tagUsers) return this.$toasted.error("Please select assignee/s")

      await this.quickAddTask()
    }
  }
}
</script>

<style scoped>
.tags-custom {
  background: white !important;
  border: lightgray 0.4px solid !important;
  border-radius: 5%;
  height: 2.5em;
  padding: 0.2rem 0 0.2rem 0.2rem;
}
</style>
<style>
.modal-content {
  background: whitesmoke !important;
}
</style>
